import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  Input,
  inject,
  signal,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ServiceChipSelectorComponent } from '../../../shared/components/service-chip-selector/service-chip-selector';
import { PromotionsService } from '../../../services/promotions/promotions.service';
import { AuthService } from '../../../services/auth';
import { Promotion } from '../../../shared/interfaces';
import flatpickr from 'flatpickr';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';

@Component({
  selector: 'app-promotion-add',
  imports: [CommonModule, ReactiveFormsModule, ServiceChipSelectorComponent],
  templateUrl: './promotion-add.html',
  styleUrl: './promotion-add.css',
})
export class PromotionAdd implements OnInit, AfterViewInit, OnDestroy {
  @Input() entityId?: string | null;

  private readonly fb = inject(FormBuilder);
  private readonly promotionsService = inject(PromotionsService);
  private readonly authService = inject(AuthService);

  loading = signal(false);
  allServices = signal(true);

  readonly audienceOptions = [
    { value: 'allClients', label: 'Todos los clientes' },
    { value: 'newClients', label: 'Clientes nuevos' },
    { value: 'vipClients', label: 'Clientes VIP' },
  ] as const;

  readonly channelOptions = [
    { value: 'email', label: 'Email' },
    { value: 'whatsapp', label: 'WhatsApp' },
  ] as const;

  readonly form = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.minLength(4)]],
    description: ['', [Validators.required, Validators.minLength(4)]],
    discountType: this.fb.nonNullable.control<'percentage' | 'fixed'>('percentage'),
    value: this.fb.nonNullable.control(10, [Validators.required, Validators.min(1)]),
    services: [''],
    startDate: ['', Validators.required],
    endDate: ['', Validators.required],
    audience: this.fb.nonNullable.control<'allClients' | 'newClients' | 'vipClients'>('allClients'),
    channels: this.fb.nonNullable.group({
      email: true,
      whatsapp: false,
      in_app: true,
    }),
    goal: this.fb.nonNullable.control(50, [Validators.min(0)]),
  });

  private startPicker?: FlatpickrInstance;
  private endPicker?: FlatpickrInstance;

  @ViewChild('startDateInput') startDateRef?: ElementRef<HTMLInputElement>;
  @ViewChild('endDateInput') endDateRef?: ElementRef<HTMLInputElement>;

  ngOnInit(): void {
    if (this.entityId) {
      this.loadPromotion();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.initDatePickers());
  }

  ngOnDestroy(): void {
    this.destroyPickers();
  }

  onAllServicesToggle(checked: boolean): void {
    this.allServices.set(checked);
    if (checked) {
      this.form.get('services')?.setValue('');
      this.form.get('services')?.clearValidators();
    } else {
      this.form.get('services')?.setValidators(Validators.required);
    }
    this.form.get('services')?.updateValueAndValidity();
  }

  private readonly currencyFormatter = new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  /** Discount as it appears on the coupon preview */
  previewValue(): string {
    const value = Number(this.form.controls.value.value) || 0;
    return this.form.controls.discountType.value === 'percentage'
      ? `${value} %`
      : this.currencyFormatter.format(value);
  }

  showError(control: string): boolean {
    const c = this.form.get(control);
    return !!(c && c.invalid && c.touched);
  }

  isValid(): boolean {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return false;
    }
    return true;
  }

  getValue(): any {
    if (!this.isValid()) return null;
    const formValue = this.form.getRawValue();
    const channels = this.channelOptions
      .filter((ch) => formValue.channels[ch.value])
      .map((ch) => ch.value);

    return {
      promotionId: this.entityId || undefined,
      name: formValue.name,
      description: formValue.description,
      discountType: formValue.discountType,
      value: formValue.value,
      services: this.allServices()
        ? []
        : formValue.services
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
      startDate: this.toIsoDate(formValue.startDate),
      endDate: this.toIsoDate(formValue.endDate),
      audience: formValue.audience,
      channels,
      goal: formValue.goal ?? 0,
    };
  }

  private loadPromotion(): void {
    const businessId = this.authService.getUserInfo()?.business_id;
    if (!businessId || !this.entityId) return;

    this.loading.set(true);
    this.promotionsService.listPromotions(businessId).subscribe({
      next: (promos) => {
        const promo = promos.find((p) => p.id === this.entityId);
        if (promo) {
          this.populateForm(promo);
        }
        this.loading.set(false);
        setTimeout(() => {
          this.initDatePickers();
          this.syncPickers();
        });
      },
      error: () => {
        this.loading.set(false);
      },
    });
  }

  private populateForm(promo: Promotion): void {
    const channels = promo.channels ?? [];
    const normalizedDiscountType = this.normalizeDiscountType(promo.discountType);
    const hasServices = promo.services && promo.services.length > 0;

    this.allServices.set(!hasServices);

    if (!hasServices) {
      this.form.get('services')?.clearValidators();
      this.form.get('services')?.updateValueAndValidity();
    }

    this.form.setValue({
      name: promo.name,
      description: promo.description,
      discountType: normalizedDiscountType,
      value: promo.value,
      services: promo.services.join(', '),
      startDate: this.toInputDate(promo.startDate),
      endDate: this.toInputDate(promo.endDate),
      audience: this.normalizeAudience(promo.audience),
      goal: promo.goal ?? 0,
      channels: {
        email: channels.includes('email'),
        whatsapp: channels.includes('whatsapp'),
        in_app: channels.includes('in_app'),
      },
    });
  }

  private toIsoDate(value: string): string {
    if (!value) return new Date().toISOString();
    const date = new Date(value);
    return isNaN(date.getTime()) ? value : date.toISOString();
  }

  private toInputDate(value: string): string {
    const date = new Date(value);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  }

  // The API returns PascalCase values (e.g. "AllClients"); the form uses camelCase
  private normalizeAudience(value: string): 'allClients' | 'newClients' | 'vipClients' {
    switch (value?.toString().toLowerCase()) {
      case 'vipclients':
      case 'vip_clients':
        return 'vipClients';
      case 'newclients':
      case 'new_clients':
        return 'newClients';
      default:
        return 'allClients';
    }
  }

  private normalizeDiscountType(value: string): 'percentage' | 'fixed' {
    switch (value?.toString().toLowerCase()) {
      case 'fixed':
      case 'fixed_amount':
      case 'amount':
        return 'fixed';
      default:
        return 'percentage';
    }
  }

  private initDatePickers(): void {
    if (this.startDateRef) {
      this.startPicker?.destroy();
      this.startPicker = this.createDatePicker(this.startDateRef.nativeElement, 'startDate');
    }
    if (this.endDateRef) {
      this.endPicker?.destroy();
      this.endPicker = this.createDatePicker(this.endDateRef.nativeElement, 'endDate');
    }
  }

  private createDatePicker(element: HTMLInputElement, control: 'startDate' | 'endDate'): FlatpickrInstance {
    const isStartDate = control === 'startDate';
    return flatpickr(element, {
      dateFormat: 'Y-m-d',
      allowInput: true,
      defaultDate: this.form.get(control)?.value ?? undefined,
      minDate: isStartDate ? undefined : this.form.get('startDate')?.value || undefined,
      locale: {
        firstDayOfWeek: 1,
        weekdays: {
          shorthand: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
          longhand: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
        },
        months: {
          shorthand: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
          longhand: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
        },
      },
      onChange: (selectedDates, dateStr) => {
        this.form.get(control)?.setValue(dateStr);
        this.form.get(control)?.markAsTouched();
        if (isStartDate && this.endPicker && selectedDates.length > 0) {
          this.endPicker.set('minDate', selectedDates[0]);
        }
      },
    });
  }

  private syncPickers(): void {
    if (this.startPicker) {
      const value = this.form.get('startDate')?.value;
      if (value) this.startPicker.setDate(value, false);
      else this.startPicker.clear();
    }
    if (this.endPicker) {
      const value = this.form.get('endDate')?.value;
      if (value) this.endPicker.setDate(value, false);
      else this.endPicker.clear();
    }
  }

  private destroyPickers(): void {
    this.startPicker?.destroy();
    this.endPicker?.destroy();
    this.startPicker = undefined;
    this.endPicker = undefined;
  }
}
